// Your GG Bharat name as a paper pass: one ticket that admits you to every world (the games, the shows, the events).
// The letters stamp in as you type; an ink stamp says whether the name is free; the bars along the bottom are drawn
// from the name itself (decoration, not a code), so every pass looks a little different. And the box you type in.
import { useMemo } from 'react';
import { HANDLE_MAX } from '../../shared/handle';
import { Wheel } from './landing/Wheel';
import type { NameState } from './Plate';

/** bars from the name: the same name always draws the same bars */
function bars(name: string) {
  let h = 2166136261;
  for (const ch of name.toLowerCase() || 'ggbharat') h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const out: [number, number][] = [];
  let x = 0;
  for (let i = 0; i < 34 && x < 118; i++) {
    h = Math.imul(h ^ (h >>> 13), 0x5bd1e995);
    const w = 1 + ((h >>> 3) & 3);
    out.push([x, w]);
    x += w + 1 + ((h >>> 7) & 1);
  }
  return out;
}

export function Pass({
  name,
  state,
  founder,
  size = 'md',
  stamp,
}: {
  name: string;
  state?: 'ok' | 'bad' | 'wait' | null;
  founder?: number | null;
  size?: 'sm' | 'md' | 'lg';
  /** the stamp's words, when not the name check's */
  stamp?: string;
}) {
  const text = (name || 'your.name').slice(0, HANDLE_MAX);
  const b = useMemo(() => bars(name), [name]);
  const words = stamp ?? (state === 'ok' ? 'Available' : state === 'bad' ? 'Taken' : state === 'wait' ? 'Checking' : '');
  return (
    <div className={`gp ${size}${state ? ` ${state}` : ''}${name ? '' : ' empty'}`} aria-label={`Name pass: ${name || 'your name'}`}>
      <div className="gp-stub" aria-hidden>
        <Wheel className="gp-wheel" />
        <span className="mono">Admit one</span>
        <span className="mono">Every world</span>
      </div>
      <div className="gp-main" aria-hidden>
        <div className="gp-top mono">
          <span>GG Bharat · Name pass</span>
          <span>Founder #{founder ? String(founder).padStart(4, '0') : '----'}</span>
        </div>
        <div className="gp-name">
          <span className="gp-at">@</span>
          {text.split('').map((c, i) => (
            <span key={`${i}${c}`} className="ch">
              {c}
            </span>
          ))}
        </div>
        <div className="gp-foot mono">
          <span>Valid in every game, show and event</span>
          <svg viewBox="0 0 120 14" className="gp-bars">
            {b.map(([x, w]) => (
              <rect key={x} x={x} width={w} height="14" />
            ))}
          </svg>
        </div>
      </div>
      {words && (
        <span className="gp-stamp" key={words}>
          {words}
        </span>
      )}
    </div>
  );
}

/** the name box: type, see your pass, see whether the name is free */
export function PassField({ value, onChange, check, autoFocus, id = 'gp-name' }: { value: string; onChange: (v: string) => void; check: NameState; autoFocus?: boolean; id?: string }) {
  return (
    <div className="gp-field">
      <label htmlFor={id}>
        <Pass name={value} state={check.status === 'ok' ? 'ok' : check.status === 'bad' ? 'bad' : check.status === 'checking' ? 'wait' : null} size="lg" />
        <span className="sr">Your GG Bharat name</span>
      </label>
      <div className="gp-input">
        <span className="mono" aria-hidden>
          @
        </span>
        <input
          id={id}
          className="gx-input mono"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\s/g, '').slice(0, HANDLE_MAX))}
          placeholder="pick your name"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          maxLength={HANDLE_MAX}
          autoFocus={autoFocus}
        />
      </div>
      <p className={`gx-namestat ${check.status}`} role="status" aria-live="polite">
        <i aria-hidden />
        {check.message || `3–${HANDLE_MAX} characters: letters, numbers, _ or .`}
      </p>
    </div>
  );
}
